import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const pad2 = (n) => String(n).padStart(2, "0");

const TimeBlock = ({ value, label, testid }) => (
  <div className="flex flex-col items-center">
    <div className="px-3 py-2 rounded-lg bg-black/70 border border-amber-500/40 shadow-[0_0_20px_rgba(212,175,55,0.3)] min-w-[3.4rem] text-center">
      <span className="font-mono font-extrabold text-3xl md:text-4xl text-amber-300 tabular-nums tracking-wider" data-testid={testid}>{value}</span>
    </div>
    <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mt-1.5">{label}</span>
  </div>
);

const Colon = () => (
  <span className="font-mono font-extrabold text-3xl md:text-4xl text-amber-400/80 animate-pulse self-start mt-3">:</span>
);

export default function PlayCard() {
  const { user, refresh } = useAuth();
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [claimable, setClaimable] = useState(0);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [busy, setBusy] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const [showTpass, setShowTpass] = useState(false);
  const [tpass, setTpass] = useState("");

  // Tick every second so the "Next box opens in" countdown updates live.
  useEffect(() => {
    const id = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    try {
      const [r, h, c] = await Promise.all([
        api.get("/me/play-card"),
        api.get("/me/play-card/history"),
        api.get("/me/play-card/claimable"),
      ]);
      setState(r.data);
      setHistory(h.data?.rows || []);
      setClaimable(Number(c.data?.claimable || 0));
    } catch (err) {
      console.warn("Play Card load failed:", err?.message);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    // Load once on mount. No polling — data refreshes only on manual page refresh.
    const tick = () => {
      if (!mounted) return;
      window.setTimeout(load, 0);
    };
    tick();
    return () => {
      mounted = false;
    };
  }, [load]);

  const claimPrincipal = async () => {
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/me/play-card/claim");
      const amt = Number(r.data?.claimed || 0);
      setMsg({ type: "ok", text: amt > 0 ? `Refunded $${amt.toFixed(2)} to your Game Fund.` : "Nothing to claim." });
      await load(); await refresh();
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  const startGame = async () => {
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/play-card/start");
      setMsg({ type: "ok", text: r.data?.message || "Game started." });
      await load(); await refresh();
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  const advance = async () => {
    if (!tpass.trim()) {
      setMsg({ type: "err", text: "Enter your transaction password." });
      return;
    }
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      await api.post("/play-card/auto-advance", { t_pass: tpass });
      setShowTpass(false);
      setTpass("");
      await load(); await refresh();
    } catch (e) {
      const status = e?.response?.status;
      const transient = !e?.response || status === 503 || status >= 500;
      if (transient) {
        // Server busy / timeout — do NOT alarm the user with an error message.
        // Quietly close and refresh so the UI reflects the true state. (Genuine
        // errors like cooldown or invalid T.Pass still show below.)
        setShowTpass(false);
        setTpass("");
        try { await load(); } catch (err) { console.warn("Play Card refresh after transient drop error failed:", err?.message); }
      } else {
        setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
      }
    } finally { setBusy(false); }
  };

  const cycle = state?.cycle || 1;
  const cardsPlayed = state?.card_index || 0;
  const cycleSchedule = state?.cycles || [
    { cycle: 1, cards_per_box: 10 },
    { cycle: 2, cards_per_box: 12 },
    { cycle: 3, cards_per_box: 14 },
  ];
  // Backend now sends a SLICE of cycles starting at (current cycle - 1). Locate the
  // current cycle inside that slice instead of indexing by absolute cycle number.
  const currentCycleDef =
    cycleSchedule.find((c) => c.cycle === cycle) || cycleSchedule[0] || { cards_per_box: 10 };
  const cardsPerBox = currentCycleDef.cards_per_box;
  const cardsPerCycle = cardsPerBox * 3;
  const boxInCycle = cardsPlayed === 0 ? 0 : Math.min(2, Math.floor((cardsPlayed - 1) / cardsPerBox));
  const currentAbsBox = (cycle - 1) * 3 + boxInCycle + 1;
  const filledInBox = Math.max(0, cardsPlayed - boxInCycle * cardsPerBox);

  // Q6 — Play Card Profit Fund (display-only preview of investor earnings against 300% cap).
  const profit = state?.profit_fund || null;

  // Live "Next box opens in" countdown derived from the global cooldown timestamp.
  const unlockMs = state?.next_box_unlock_at ? new Date(state.next_box_unlock_at).getTime() : 0;
  const remainingSec = unlockMs ? Math.max(0, Math.ceil((unlockMs - nowTs) / 1000)) : 0;
  const onCooldown = remainingSec > 0;
  const cdH = Math.floor(remainingSec / 3600);
  const cdM = Math.floor((remainingSec % 3600) / 60);
  const cdS = remainingSec % 60;

  return (
    <div className="space-y-8">
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
        <div className="lt-card p-5"><div className="overline">Cycle</div><div className="font-display text-4xl tabular mt-2" data-testid="play-cycle">{cycle}</div><div className="text-[10px] text-neutral-500 mt-1">3 boxes per cycle</div></div>
        <div className="lt-card p-5"><div className="overline">Box No</div><div className="font-display text-4xl tabular mt-2" data-testid="play-box">{currentAbsBox}</div><div className="text-[10px] text-neutral-500 mt-1">unlimited cycles</div></div>
        <div className="lt-card p-5"><div className="overline">Capacity</div><div className="font-display text-4xl tabular mt-2 gold-text" data-testid="play-capacity">{cardsPerBox}</div><div className="text-[10px] text-neutral-500 mt-1">cards per box (Cycle {cycle})</div></div>
        <div className="lt-card p-5"><div className="overline">Filled</div><div className="font-display text-4xl tabular mt-2 gold-text" data-testid="play-filled">{filledInBox} / {cardsPerBox}</div><div className="text-[10px] text-neutral-500 mt-1">in current box</div></div>
        <div className="lt-card p-5"><div className="overline">In-Play Principal</div><div className="font-display text-4xl tabular mt-2">${(state?.principal || 0).toFixed(2)}</div><div className="text-[10px] text-neutral-500 mt-1">Locked in your open boxes (refunds 100% on box close)</div></div>
      </div>

      {profit && profit.principal_paid_in > 0 && (
        <div className="lt-card p-6 border border-amber-500/30" data-testid="play-profit-fund-card">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
            <div>
              <div className="overline text-amber-300">Play Card Profit Fund</div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="overline">Investor Earned</div>
              <div className="font-display text-2xl tabular mt-1 gold-text" data-testid="play-profit-earned">${profit.investor_earned.toFixed(2)}</div>
              <div className="text-[10px] text-neutral-500 mt-1">10% × boxes closed</div>
            </div>
            <div>
              <div className="overline">Recycled to LP</div>
              <div className="font-display text-2xl tabular mt-1" data-testid="play-profit-recycled">${(profit.lp_recycled || 0).toFixed(2)}</div>
              <div className="text-[10px] text-neutral-500 mt-1">$5 moves to LP per $15 earned</div>
            </div>
            <div>
              <div className="overline">Next $5 Recycle At</div>
              <div className="font-display text-2xl tabular mt-1" data-testid="play-profit-next-recycle">${(profit.next_recycle_at || 0).toFixed(2)}</div>
              <div className="text-[10px] text-neutral-500 mt-1">${(profit.to_next_recycle || 0).toFixed(2)} more to earn</div>
            </div>
          </div>
        </div>
      )}

      <div className="lt-card p-6 flex flex-col gap-5" data-testid="play-action-card">
        <div className="flex items-center justify-between gap-4 flex-wrap pb-2 border-b border-white/5">
          <div>
            <div className="overline text-amber-300">Your Game Fund (available to play)</div>
            <div className="font-display text-4xl tabular mt-1 gold-text" data-testid="play-game-fund">${Number(state?.game_fund || 0).toFixed(2)}</div>
            <div className="text-[11px] text-neutral-500 mt-1">Each card drop costs $5 from this balance. Top up via Transfer → Deposit to Game Fund.</div>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {!state?.active ? (
            <button onClick={startGame} disabled={busy || !user?.package_value} className="lt-btn-primary pulse-gold" data-testid="play-start-btn">
              {user?.package_value ? "Start Game" : "Activate package first"}
            </button>
          ) : (
            <button onClick={() => { setMsg({ type: "", text: "" }); setTpass(""); setShowTpass(true); }} disabled={busy || onCooldown || Number(state?.game_fund || 0) < 5} className="lt-btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="play-advance-btn">
              Drop card ($5)
            </button>
          )}
          {state?.active && !onCooldown && Number(state?.game_fund || 0) < 5 && (
            <span className="text-sm text-red-400" data-testid="play-lowfund-msg">Game Fund below $5 — add funds via Transfer → Deposit to Game Fund.</span>
          )}
          {msg.text && !onCooldown && <span className={`text-sm ${msg.type === "ok" ? "gold-text" : "text-red-400"}`} data-testid="play-msg">{msg.text}</span>}
        </div>

        {state?.active && onCooldown && (
          <div className="rounded-xl border border-amber-500/30 bg-gradient-to-b from-amber-500/[0.06] to-transparent px-5 py-4" data-testid="play-next-box-timer">
            <div className="overline text-amber-300 mb-3">⏱ Next box opens in</div>
            <div className="flex items-center gap-2">
              {cdH > 0 && (<><TimeBlock value={pad2(cdH)} label="Hours" testid="cd-hours" /><Colon /></>)}
              <TimeBlock value={pad2(cdM)} label="Minutes" testid="cd-mins" />
              <Colon />
              <TimeBlock value={pad2(cdS)} label="Seconds" testid="cd-secs" />
            </div>
            <div className="text-xs text-neutral-500 mt-3">The board has a shared cooldown after each box closes. The Drop card button unlocks automatically when the timer hits zero.</div>
          </div>
        )}
      </div>

      {showTpass && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="play-tpass-modal" onClick={() => !busy && setShowTpass(false)}>
          <div className="lt-card p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="overline text-amber-300 mb-1">Confirm card drop</div>
            <div className="text-sm text-neutral-400 mb-4">Enter your transaction password to drop a card ($5 from your Game Fund).</div>
            <label className="overline block mb-2">Transaction password</label>
            <input
              type="password"
              autoFocus
              className="lt-input"
              value={tpass}
              onChange={(e) => setTpass(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !busy) advance(); }}
              data-testid="play-tpass-input"
            />
            {msg.text && <div className={`mt-3 text-sm ${msg.type === "ok" ? "gold-text" : "text-red-400"}`} data-testid="play-tpass-msg">{msg.text}</div>}
            <div className="flex items-center gap-3 mt-5">
              <button onClick={advance} disabled={busy} className="lt-btn-primary disabled:opacity-50" data-testid="play-tpass-confirm-btn">
                {busy ? "Dropping…" : "Confirm Drop ($5)"}
              </button>
              <button onClick={() => setShowTpass(false)} disabled={busy} className="text-sm text-neutral-400 hover:text-neutral-200" data-testid="play-tpass-cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {claimable > 0 && (
        <div className="lt-card p-6 border border-yellow-500/40 flex items-center gap-4 flex-wrap" data-testid="play-claim-card">
          <div className="flex-1 min-w-[200px]">
            <div className="overline text-yellow-400">Claim available</div>
            <div className="font-display text-2xl tabular gold-text mt-1" data-testid="play-claim-amount">${claimable.toFixed(2)}</div>
            <div className="text-xs text-neutral-400 mt-1">
              Admin reset the round. Refund your parked Play Card principal back to your NFT Card Fund.
            </div>
          </div>
          <button onClick={claimPrincipal} disabled={busy} className="lt-btn-primary" data-testid="play-claim-btn">
            Claim ${claimable.toFixed(2)} to Game Fund
          </button>
        </div>
      )}

      <div className="lt-card p-0 overflow-hidden" data-testid="play-history-card">
        <div className="px-6 py-4 border-b border-white/5">
          <div className="overline">Play Card History</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-white/5">
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2">Cycle / Box</th>
                <th className="px-4 py-2">Cost</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-6 text-center text-neutral-500">No drops yet — click <span className="gold-text">Drop card</span> to begin.</td></tr>
              )}
              {history.map((h) => (
                <tr key={h.id} className="border-b border-white/[0.03]" data-testid={`play-hist-row-${h.global_drop_no || h.drop_no}`}>
                  <td className="px-4 py-2 text-neutral-400">{new Date(h.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2 tabular">C{h.cycle} · Box {h.box}</td>
                  <td className="px-4 py-2 tabular">${Number(h.cost || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
